import DOMPurify, { Config } from 'dompurify';
import { sanitizeStyleBlock, sanitizeInlineStyle } from './sanitizeStyle';
import { getRemoveBreadcrumb, sanitizeLogger } from './sanitizeLogger';
import { DEFAULT_CONFIG_SANITIZE_HTML } from './config';

/**
 * Добавление кастомных хуков DOMPurify для дополнительной очистки атрибутов.
 *
 * Хуки регистрируются один раз.
 */
let hooksInstalled = false;
if (!hooksInstalled && DOMPurify) {
	DOMPurify.addHook('uponSanitizeElement', (node, data) => {
		/**
		 * - `uponSanitizeElement`:
		 *   - Срабатывает для каждого тега до того, как он попадёт в DOM.
		 *   - Если тег — `<style>`, то его содержимое очищается через {@link sanitizeStyleBlock}.
		 *   - Если в `<style>` не остаётся безопасных деклараций, тег полностью удаляется.
		 */
		if (data?.tagName?.toLowerCase() === 'style') {
			const cleaned = sanitizeStyleBlock(node.textContent);

			if (cleaned) {
				node.textContent = cleaned;
			} else {
				node.parentNode?.removeChild(node);
			}
		}
	});

	/**
	 * - `uponSanitizeAttribute`:
	 *   - Срабатывает для каждого атрибута до того, как он попадёт в DOM.
	 *   - Если атрибут — `style`, то его значение очищается через {@link sanitizeInlineStyle}.
	 *   - Если в style не остаётся безопасных деклараций, атрибут полностью удаляется.
	 */
	DOMPurify.addHook('uponSanitizeAttribute', (_, data) => {
		if (data.attrName?.toLowerCase() === 'style') {
			const cleaned = sanitizeInlineStyle(String(data.attrValue ?? ''));
			if (cleaned) {
				data.attrValue = cleaned;
			} else {
				data.keepAttr = false;
			}
		}
	});

	/**
	 * - `afterSanitizeAttributes`:
	 *   - Срабатывает после очистки атрибутов элемента.
	 *   - Если у ссылки (`<a>`) стоит `target="_blank"`, автоматически добавляется `rel="noopener noreferrer"`.
	 *     Это предотвращает атаку tab-nabbing (новое окно не может получить доступ к opener).
	 */
	DOMPurify.addHook('afterSanitizeAttributes', node => {
		if (
			'target' in node &&
			node.getAttribute('target') === '_blank' &&
			node.getAttribute('rel') !== 'noopener noreferrer'
		) {
			sanitizeLogger({
				message: `The tag: "${node.nodeName?.toLowerCase()}", contains the target="_blank" attribute without the rel="noopener noreferrer" attribute.`
			});
			node.setAttribute('rel', 'noopener noreferrer');
		}
	});
	hooksInstalled = true;
}

/**
 * Очищает HTML с помощью DOMPurify {@link https://github.com/cure53/DOMPurify/tree/main}.
 *
 * - Применяет базовый `defaultConfig`.
 * - Использует хуки для очистки атрибута `style` через {@link sanitizeInlineStyle}.
 * - Использует хуки для очистки тега `<style>` через {@link sanitizeStyleBlock}.
 * - Добавляет защиту для ссылок в href и навешивает rel=noopener noreferrer на target=_blank.
 * - Логирует удаленные теги и атрибуты в Sentry.
 *
 * @param html HTML для очистки.
 * @param config (опционально) Дополнительный или переопределённый конфиг DOMPurify.
 * @returns Очищенный HTML.
 */
export const sanitizeHtml = (html: string, config?: Config): string => {
	if (!DOMPurify || !html) {
		return '';
	}
	/**
	 * Если true, то добавляем в конфиг WHOLE_DOCUMENT: true
	 * И тогда обрабатывает как цельный документ, сохраняя теги 'html', 'head', 'body', 'style'
	 */
	const whole = [/<!doctype html/i, /<html[\s>]/i, /<head[\s>]/i, /<body[\s>]/i].some(re => re.test(html));

	const cleaned = DOMPurify.sanitize(html, {
		...DEFAULT_CONFIG_SANITIZE_HTML,
		WHOLE_DOCUMENT: whole,
		...config
	}) as string;

	/** Логирует удаленные теги и атрибуты */
	DOMPurify.removed.forEach(remove => {
		sanitizeLogger(getRemoveBreadcrumb(remove, html));
	});

	return cleaned;
};
